import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DataContext from '../../Contexts/DataContext';
import { useAuth } from '../../Contexts/AuthContext';
import MasterStatForm from './MasterStatForm';
import { toast } from 'react-toastify';

export default function MasterStatTab({ isDataFullyLoaded = true }) {
  const auth = useAuth();
  const activeUser = auth.activeUser;
  const context = useContext(DataContext);
  const {
    activeProjectAttributes,
    activeProjectInfo,
    createMasterStat,
    updateMasterStat,
    deleteMasterStat,
    buildAgGridTable,
  } = context;

  const [showForm, setShowForm] = useState(false);
  const [editingMasterStat, setEditingMasterStat] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [masterStatToDelete, setMasterStatToDelete] = useState(null);

  const masterStats = activeProjectAttributes.masterStats || [];

  // Note: Account data loading is handled by ProjectHomeAgGrid component
  // The Master Stat form will extract entities/departments from agGridData when available

  const handleAddNew = () => {
    if (!isDataFullyLoaded) {
      toast.warning('Please wait for project data to finish loading');
      return;
    }
    setEditingMasterStat(null);
    setShowForm(true);
  };

  const handleEdit = (masterStat) => {
    if (!isDataFullyLoaded) {
      toast.warning('Please wait for project data to finish loading');
      return;
    }
    setEditingMasterStat(masterStat);
    setShowForm(true);
  };

  const handleDeleteClick = (masterStat) => {
    setMasterStatToDelete(masterStat);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (masterStatToDelete) {
      const success = await deleteMasterStat(
        masterStatToDelete.id,
        activeProjectInfo.id,
        activeUser.accessToken
      );

      if (success) {
        toast.success('Master Stat deleted successfully');
        setDeleteDialogOpen(false);
        setMasterStatToDelete(null);
      } else {
        toast.error('Failed to delete Master Stat');
      }
    }
  };

  const handleSave = async (masterStatData) => {
    let success;

    if (editingMasterStat) {
      // Update existing Master Stat
      success = await updateMasterStat(
        editingMasterStat.id,
        masterStatData,
        activeUser.id,
        activeProjectInfo.id,
        activeUser.accessToken
      );

      if (success) {
        // Invalidate cache to force refetch of audit trail after edit
        if (context?.invalidateAuditTrailCache && editingMasterStat.id) {
          context.invalidateAuditTrailCache(editingMasterStat.id, 'masterStat');
        }
        toast.success('Master Stat updated successfully');
      } else {
        toast.error('Failed to update Master Stat');
      }
    } else {
      // Create new Master Stat
      success = await createMasterStat(
        masterStatData,
        activeUser.id,
        activeProjectInfo.id,
        activeUser.accessToken
      );

      if (success) {
        toast.success('Master Stat created successfully');
      } else {
        toast.error('Failed to create Master Stat');
      }
    }

    if (success) {
      setShowForm(false);
      setEditingMasterStat(null);
    }

    return success;
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMasterStat(null);
  };

  // Get account name from GL key
  const getAccountName = (glKey) => {
    if (!activeProjectAttributes.accountsMetadata) return glKey;
    
    const account = activeProjectAttributes.accountsMetadata.find(
      acc => `${acc.entity_id}-${acc.department_id}-${acc.account_id}` === glKey
    );
    
    return account ? account.account_name : glKey;
  };

  if (showForm) {
    return (
      <MasterStatForm
        masterStat={editingMasterStat}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Master Stats</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleIcon />}
          onClick={handleAddNew}
          className="add-new-button"
          disabled={!isDataFullyLoaded}
        >
          Add New Master Stat
        </Button>
      </Box>

      {!isDataFullyLoaded && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            Loading project data... Please wait before creating or editing Master Stats.
          </Typography>
        </Box>
      )}

      {masterStats.length === 0 ? (
        <Box className="empty-state">
          <FolderOpenIcon className="empty-icon" />
          <Typography className="empty-message">
            No Master Stats created yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click "Add New Master Stat" to create your first Master Stat grouping
          </Typography>
        </Box>
      ) : (
        <Box className="master-stat-list">
          {masterStats.map((masterStat) => (
            <Card key={masterStat.id} className="master-stat-card" elevation={2}>
              <CardContent>
                <Box className="master-stat-header">
                  <Box>
                    <Typography className="master-stat-title">
                      {masterStat.master_stat_name}
                    </Typography>
                    <Typography className="gl-key">
                      GL Key: {masterStat.gl_key}
                    </Typography>
                  </Box>
                  <Box className="action-buttons">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(masterStat)}
                      title="Edit Master Stat"
                      disabled={!isDataFullyLoaded}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(masterStat)}
                      title="Delete Master Stat"
                      disabled={!isDataFullyLoaded}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Box className="child-stats-list">
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Child Stats ({masterStat.child_stats?.length || 0}):
                  </Typography>
                  {masterStat.child_stats?.map((childGlKey, idx) => (
                    <Box key={idx} className="child-stat-item">
                      • {getAccountName(childGlKey)}
                      <span className="child-gl-key">({childGlKey})</span>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Master Stat</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{masterStatToDelete?.master_stat_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
